import { useCallback, useEffect, useMemo, useState } from "react";

// @ts-expect-error We trust anyone using this has reanimated installed (breaks backend if reanimated is a dependency)
import Animated from "react-native-reanimated";

import CompositionsLight, { CompositionKey } from "../../../CompositionsLight";
import { clearFetchCache } from "../../../helpers/fetchSkiaFont/helpers/cachedFetchWithRetry";
import useLoadSkiaWebPromise from "../hooks/useLoadSkiaWebPromise";
import { PlayerProps } from "../types";
import PlayerContentLoader from "./PlayerContentLoader";

const PlayerContent = <TComp extends CompositionKey>({
	composition,
	props,
	loading,
	LoadingComponent,
	loadingContainerStyle,
	processingProgress,
}: PlayerProps<TComp>) => {
	const curComposition = useMemo(
		// Using compositions light here since web hasn't necessarily loaded wasm
		() => CompositionsLight[composition],
		[composition],
	);

	const [containerSize, setContainerSize] = useState<null | {
		width: number;
		height: number;
	}>(null);
	const [scale, setScale] = useState<null | number>(null);
	const handleLayout = useCallback((event: any) => {
		const { width, height } = event.nativeEvent.layout as {
			width: number;
			height: number;
		};
		const scale = Math.min(
			width / curComposition.videoConfig.width,
			height / curComposition.videoConfig.height,
		);

		setScale(scale);

		const aspectRatio = curComposition.videoConfig.width / curComposition.videoConfig.height;
		if (width > height) {
			setContainerSize({
				width: height * aspectRatio,
				height,
			});
		} else {
			setContainerSize({
				width,
				height: width / aspectRatio,
			});
		}
	}, []);
	const [isReady, setIsReady] = useState(false);
	// Want to start loading Skia Web even if props isn't ready yet
	useLoadSkiaWebPromise();
	useEffect(() => {
		return () => {
			clearFetchCache();
		};
	}, []);

	return (
		<Animated.View
			onLayout={handleLayout}
			style={{
				width: "100%",
				height: "100%",
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			{!!(props && scale) && (
				<Animated.View
					style={{
						transform: [{ scale: scale ?? 1 }],
					}}
				>
					<PlayerContentLoader
						composition={composition}
						props={props}
						onReady={() => setIsReady(true)}
						processingProgress={processingProgress}
					/>
				</Animated.View>
			)}
			{containerSize && (!isReady || loading) && (
				<Animated.View
					style={[
						loadingContainerStyle,
						{
							position: "absolute",
							width: containerSize.width,
							height: containerSize.height,
						},
					]}
				>
					{LoadingComponent && <LoadingComponent />}
				</Animated.View>
			)}
		</Animated.View>
	);
};
export default PlayerContent;
